import { fetchUtils } from 'react-admin';

// ─────────────────────────────────────────────────────────────────────────────
// Llamadas del BO a la API de la web nueva. Los mails de auth ya no salen por
// las plantillas de Google: la web genera el link con el Admin SDK y envía por
// SendGrid con la plantilla de marca. El BO se autentica con el ID token de
// Firebase del admin logueado (mismo patrón que el aviso de transacciones): la
// web verifica el token y que users/{uid}.isAdmin sea true.
//
// Kinds vigentes (espejo de POST /api/admin/auth-email): 'reset' (restablecer
// contraseña), 'suspended' y 'reactivated' (avisos de estado de cuenta). El
// kind 'verify' se ELIMINÓ junto con el mail de verificación (decisión de
// producto 03/07: la invitación ES la verificación) — el endpoint lo rechaza.
//
// Requiere REACT_APP_YIELDBOX_APP_BASE_URL (ya usada por el mail de
// transacciones). Si falta o el endpoint falla, se tira: el caller decide el
// fallback y el aviso al admin.
// ─────────────────────────────────────────────────────────────────────────────

export async function sendAuthEmailViaWeb(
  authProvider: any,
  kind: 'reset' | 'suspended' | 'reactivated',
  email: string
): Promise<void> {
  let base = process.env.REACT_APP_YIELDBOX_APP_BASE_URL;
  if (!base) throw new Error('REACT_APP_YIELDBOX_APP_BASE_URL sin configurar');
  let token = await authProvider.getJWTToken();
  await fetchUtils.fetchJson(`${base}/api/admin/auth-email`, {
    method: 'POST',
    body: JSON.stringify({ kind, email }),
    user: { authenticated: true, token },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// fase46 — Purga TOTAL de un usuario, ejecutada por la WEB con el Admin SDK
// (POST /api/admin/purge-user). El borrado client-side anterior tenía dos
// agujeros que el smoke test destapó: el SDK del navegador no puede borrar la
// cuenta de Firebase Auth (quedaba viva, y un re-registro con ese email
// heredaba restos) y el censo de colecciones era parcial (sobrevivían las
// subcolecciones del usuario, snapshots, telemetría y soporte). El server
// borra TODO — Auth incluida — de forma idempotente, y escribe él mismo el
// registro de auditoría con el desglose. Devuelve el resumen para mostrarlo.
// ─────────────────────────────────────────────────────────────────────────────

export type PurgeResult = {
  ok?: boolean;
  // fase51 — false: pasada parcial (presupuesto de tiempo agotado en la web);
  // el caller re-llama hasta true/undefined. Ver doPurge en user-admin-actions.
  done?: boolean;
  authDeleted: boolean;
  documents?: number;
  deleted?: Record<string, number>;
  error?: string;
};

export async function purgeUserViaWeb(
  authProvider: any,
  uid: string
): Promise<PurgeResult> {
  let base = process.env.REACT_APP_YIELDBOX_APP_BASE_URL;
  if (!base) throw new Error('REACT_APP_YIELDBOX_APP_BASE_URL sin configurar');
  let token = await authProvider.getJWTToken();
  let { json } = await fetchUtils.fetchJson(`${base}/api/admin/purge-user`, {
    method: 'POST',
    body: JSON.stringify({ uid }),
    user: { authenticated: true, token },
  });
  return json as PurgeResult;
}

// ─────────────────────────────────────────────────────────────────────────────
// fase50 — RECONCILIAR LA RESERVA de un cliente (la puerta de salida del freeze).
//
// Cuando el robot detecta que el libro no cuadra en un retiro con reserva (el
// monto se editó después de reservar, la reserva no alcanza…), NO mueve un
// centavo: congela la transacción y alerta. Correcto — pero esos fondos quedaban
// atrapados en `investments.reserved` PARA SIEMPRE: el cliente los veía como "en
// proceso de retiro", no podía operarlos, y no había forma de destrabarlos desde
// acá. La única salida era editar Firestore a mano.
//
// Este endpoint recalcula la reserva desde la verdad del libro (los retiros que
// están REALMENTE en curso) y devuelve el residuo al disponible del cliente. La
// tenencia total no cambia: la plata se mueve de un bolsillo al otro.
//
// `dryRun: true` sólo diagnostica (no escribe): así el diálogo puede mostrar
// exactamente qué se va a devolver ANTES de tocar nada.
// ─────────────────────────────────────────────────────────────────────────────

export type ReconcileLeg = {
  ticker: string;
  delta: number;
  bookReserved: number;
  liveReserved: number;
  frozenTxIds: string[];
};

export type ReconcileResult = {
  ok?: boolean;
  dryRun: boolean;
  changed: number;
  frozenClosed: number;
  legs: ReconcileLeg[];
  error?: string;
};

export async function reconcileReserveViaWeb(
  authProvider: any,
  uid: string,
  dryRun = false
): Promise<ReconcileResult> {
  let base = process.env.REACT_APP_YIELDBOX_APP_BASE_URL;
  if (!base) throw new Error('REACT_APP_YIELDBOX_APP_BASE_URL sin configurar');
  let token = await authProvider.getJWTToken();
  let { json } = await fetchUtils.fetchJson(`${base}/api/admin/reconcile-reserve`, {
    method: 'POST',
    body: JSON.stringify({ uid, dryRun }),
    user: { authenticated: true, token },
  });
  return json as ReconcileResult;
}

// ─────────────────────────────────────────────────────────────────────────────
// 2FA (Google Authenticator) del BO contra la API de la web. Mismo patrón de
// auth que arriba (ID token del admin). El secreto TOTP nunca baja al navegador:
// la web sólo devuelve el QR y, al verificar, un custom token con el claim
// `boMfa` que el BO canjea. Ver src/core/providers/auth.ts (startTwoFactor /
// completeTwoFactor) y los endpoints /api/admin/2fa/* de la web.
// ─────────────────────────────────────────────────────────────────────────────

export async function fetch2faSetup(
  authProvider: any
): Promise<{ qrDataUrl: string; alreadyEnrolled: boolean }> {
  let base = process.env.REACT_APP_YIELDBOX_APP_BASE_URL;
  if (!base) throw new Error('REACT_APP_YIELDBOX_APP_BASE_URL sin configurar');
  let token = await authProvider.getJWTToken();
  let { json } = await fetchUtils.fetchJson(`${base}/api/admin/2fa/setup`, {
    method: 'GET',
    user: { authenticated: true, token },
  });
  return json as { qrDataUrl: string; alreadyEnrolled: boolean };
}

export async function verify2faCode(
  authProvider: any,
  code: string
): Promise<{ ok: boolean; token: string; recoveryCodes?: string[] }> {
  let base = process.env.REACT_APP_YIELDBOX_APP_BASE_URL;
  if (!base) throw new Error('REACT_APP_YIELDBOX_APP_BASE_URL sin configurar');
  let token = await authProvider.getJWTToken();
  let { json } = await fetchUtils.fetchJson(`${base}/api/admin/2fa/verify`, {
    method: 'POST',
    body: JSON.stringify({ code }),
    user: { authenticated: true, token },
  });
  return json as { ok: boolean; token: string; recoveryCodes?: string[] };
}
